import { AdmissionCycle } from '../../../types/admissions';
import type { ChipTone } from '../../common/StatusChip';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { everyClassStopped } from '../../../utils/admissionClasses';

/**
 * How a cycle's intake reads to staff — the cycles list and the cycle's own
 * page say it the same way. The server decides each channel's state
 * (cycle.intakeNow); this only words it.
 */

const fmt = (iso?: string | null) => (iso ? dateTimeUtils.formatDateForDisplay(iso) : '');

/** One line on whether the cycle takes new applications, for the list and the page header. */
export const intakeHeadline = (cycle: AdmissionCycle): { label: string; tone: ChipTone } | null => {
  if (cycle.status !== 'Open') return null;
  const { online, walkIn } = cycle.intakeNow;
  if (everyClassStopped(cycle)) {
    return { label: 'No class taking applications', tone: 'warning' };
  }
  if (online.open && walkIn.open) return { label: 'Taking applications', tone: 'success' };
  if (online.open) return { label: 'Online only', tone: 'info' };
  if (walkIn.open) return { label: online.state === 'not_yet' ? 'Walk-ins only · online opens soon' : 'Walk-ins only', tone: 'info' };
  return { label: 'Not taking applications', tone: 'warning' };
};

/** The online form's state, in a sentence. */
export const onlineLine = (cycle: AdmissionCycle): string => {
  const { online, window } = cycle.intakeNow;
  const close = cycle.schedule?.applicationClose;
  switch (online.state) {
    case 'open':
      if (!close) return 'Taking applications — no closing date';
      return window.daysLeft === 0
        ? 'Taking applications — closes today'
        : `Taking applications until ${fmt(close)} · ${window.daysLeft} day${window.daysLeft === 1 ? '' : 's'} left`;
    case 'not_yet': return `Opens on ${fmt(cycle.schedule?.applicationOpen)}`;
    case 'past_deadline': return `Closed on ${fmt(close)} — move the close date in Settings to take more`;
    case 'paused': return 'Paused — parents who open the link are asked to contact the office';
    case 'not_started': return cycle.intake.online ? 'Goes live when the cycle opens' : 'Stays off when the cycle opens — switch it on to take applications online';
    default: return 'The cycle is closed';
  }
};

/** Walk-ins and bulk upload, in a sentence. */
export const walkInLine = (cycle: AdmissionCycle): string => {
  switch (cycle.intakeNow.walkIn.state) {
    case 'open': return 'Staff can add applicants at the desk or by bulk upload — the online dates don’t apply';
    case 'paused': return 'Stopped — staff can’t add new applicants';
    case 'not_started': return cycle.intake.walkIn ? 'Starts when the cycle opens' : 'Stays off when the cycle opens — switch it on to add applicants at the desk';
    default: return 'The cycle is closed';
  }
};

/** What stopping intake does — asked before it happens on an open cycle. */
export const STOP_CONFIRM = {
  all: {
    title: 'Stop all new applications?',
    message: 'The online form tells parents applications are paused, and staff can’t add walk-ins or bulk upload. Applicants already in the cycle are processed as usual. You can resume any time.',
    confirmText: 'Stop applications',
  },
  online: {
    title: 'Pause the online form?',
    message: 'Parents who open the link are asked to contact the office, and anyone halfway through the form won’t be able to submit it. Walk-ins carry on.',
    confirmText: 'Pause online form',
  },
  walkIn: {
    title: 'Stop walk-ins?',
    message: 'Staff can’t add applicants at the desk or by bulk upload until you switch it back on. The online form carries on.',
    confirmText: 'Stop walk-ins',
  },
} as const;

export const stopClassConfirm = (className: string) => ({
  title: `Stop ${className} taking applications?`,
  message: `${className} disappears from the online form, the walk-in form and new bulk templates. Its applicants are processed as usual — switch it back on any time.`,
  confirmText: `Stop ${className}`,
});
